import { defineComponent as _defineComponent } from 'vue'
import type { UpcomingEvent } from '@/features/sports/types/interfaces';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import type { ToggleMySportsNotificationPayload } from '@/features/sports/types/enums';

import { ObjectType, SportEventStatus } from '@/@types/graphql-types';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import CompetitorLogo from '@/features/sports/components/ui/CompetitorLogo.vue';
import NoOfferNotifier from '@/features/sports/components/global/NoOfferNotifier.vue';
import GameEventStatus from '@/features/sports/components/ui/GameEventStatus.vue';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { formatDate as dateFormatter, formatTime as timeFormatter } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { buildUrlForDomain, Domain } from '@/routing/helpers/subdomain-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'MultiStepEventEncounter',
  props: {
    event: null,
    listIndex: null,
    isGlobalPage: { type: Boolean },
    withCompetitionInHeader: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { event: UpcomingEvent, listIndex?: number, isGlobalPage?: boolean, withCompetitionInHeader?: boolean, additionalContexts: SnowplowContext[] };



const { language, locales, webLocale, country } = storeToRefs(useLanguageStore());
const { timeZone } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());

const eventPath = computed(() => {
	if (props.event.fullPath === '' || props.event.fullPath == null) return null;

	const path = { path: props.event.fullPath, query: {} };

	// Add stage query parameter for better context (consistent on server and client)
	const { name, number } = props.event.sportEvent.content?.competitionStage;
	if (name) {
		path.query = { stage: `${name} ${number || ''}` };
	}

	return path;
});

const sportContext = computed(() => {
	return SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, props.event.sportEvent);
});

const sport = computed(() => props.event?.sport);

const isTennisDoubles = computed(() => sport.value === 'TENNIS' && props.event?.lineItems.length > 2);

const formattedLineItemNames = computed(() => {
	if (!isTennisDoubles.value) {
		return props.event?.lineItems;
	}

	return [
		{
			text: `${shortName(props.event?.lineItems[0]?.text)} / ${shortName(props.event?.lineItems[1]?.text)}`,
		},
		{
			text: `${shortName(props.event?.lineItems[2].text)} / ${shortName(
				props.event?.lineItems[3]?.text ?? props.event?.lineItems[2].text
			)}`,
		},
	];
});

const eventStatus = computed(() => props.event?.sportEvent?.content?.status);

const isLive = computed(() => [SportEventStatus.Live].includes(eventStatus.value));

const isEnded = computed(() => [SportEventStatus.Ended, SportEventStatus.Closed].includes(eventStatus.value));

const isNotUpcoming = computed(() => {
	return [
		SportEventStatus.Cancelled,
		SportEventStatus.Closed,
		SportEventStatus.Delayed,
		SportEventStatus.Ended,
		SportEventStatus.Live,
		SportEventStatus.Postponed,
		SportEventStatus.Suspended,
	].includes(eventStatus.value);
});

const watchNowOffer = computed(() => props.event.watchNowOffer);

const notifyMePayload = computed<ToggleMySportsNotificationPayload>(() => {
	return {
		eventId: props.event.id,
		isNotificationActive: !!props.event.mysportlistEntry?.createdAt,
	};
});

function shortName(name: string) {
	if (!name) return '';

	const firstNameInital = name.split('')[0];
	const surname = name.split(' ').slice(1).join(' ');

	return `${surname} ${firstNameInital}`;
}

function formatDate(ts: Date) {
	return dateFormatter(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

function formatTime(ts: Date) {
	return timeFormatter(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

const offerClickoutUrl = computed(() => {
	return new ClickoutUrl(watchNowOffer.value)
		.addContexts(sportContext.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('uct_sport', props.event?.sport)
		.setCanary(xServeTrack.value);
});

function handleClickOut() {
	if (watchNowOffer.value == null) return;

	trackClickOutSports(watchNowOffer.value, 0, props.listIndex, watchNowOffer.value.sportObjectType, 'card-v2', [
		sportContext.value,
	]);
}

function navigateToEvent() {
	if (eventPath.value == null) return;

	handleCardClick();

	// Build the full URL for sports subdomain
	const url = buildUrlForDomain(eventPath.value.path, Domain.SPORTS);
	window.location.href = url;
}

function handleCardClick() {
	if (props.isGlobalPage) return;

	TrackingHelper.trackEvent('userinteraction', { action: 'event_clicked', property: 'card-v2', useLastRoute: true }, [
		sportContext.value,
		...props.additionalContexts,
	]);
}

async function handleNoOfferNotifierClick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'notify_me_clicked', property: 'card-v2', useLastRoute: true },
		[sportContext.value, ...props.additionalContexts]
	);
}

function trackCompetitionClick() {
	const sportContext = new SnowplowSportContext(props.event.sport, ObjectType.SportCompetition);
	const pageTypeContext = TrackingHelper.getPageTypeContext(true);

	TrackingHelper.trackEvent('userinteraction', { action: 'competition_clicked', property: 'card-v2' }, [
		sportContext,
		pageTypeContext,
		...props.additionalContexts,
	]);
}

return { __sfc: true,props, language, locales, webLocale, country, timeZone, xServeTrack, eventPath, sportContext, sport, isTennisDoubles, formattedLineItemNames, eventStatus, isLive, isEnded, isNotUpcoming, watchNowOffer, notifyMePayload, shortName, formatDate, formatTime, offerClickoutUrl, handleClickOut, navigateToEvent, handleCardClick, handleNoOfferNotifierClick, trackCompetitionClick, ProviderIcon, CompetitorLogo, NoOfferNotifier, GameEventStatus, buildUrlForDomain, Domain }
}

})